import { AddSectionDTO } from "@/composables/section/models/add-section.dto";
import { Section } from "@/composables/section/models/section.model";
import { UpdateSectionDTO } from "@/composables/section/models/update-section.dto";
import ForgotPasswordModel from "@/helpers/models/forgotpassword.model";
import { LoginModel } from "@/helpers/models/login.model";
import { ResendVerificationDTO } from "@/helpers/models/resendverification.dto";
import { ResetPasswordModel } from "@/helpers/models/resetpassword.model";
import {
  SignupModel,
  SignupWaitlistModel,
} from "@/helpers/models/signup.model";
import { VerifyFirebaseToken } from "@/helpers/models/verify_firebase";
import api from "@/interceptors/api";
import { eventAPI } from "@/interceptors/event-api";
import { PredictionOHLC } from "@/views/private/profile-screen/components/PredictionOHLC/dto/prediction-ohlc.model";
import PostsModel from "@/views/private/timeline/models/posts.model";
import UpdatePostModel from "@/views/private/timeline/models/updatepost.model";
import CommentsModel from "@/views/private/timeline/models/comments.model";
import { LikePostModel } from "@/views/private/timeline/models/likepost.model";
import { UnlikeModel } from "@/views/private/timeline/models/unlike.model";
import cacheApi from "@/interceptors/cache-api";
import AddTopFriendDTO from "@/components/header/search-container/addtopfriendsdto";
import { FollowStockModel } from "../composables/followstock.model";
import SocialAccountVerificationDTO from "@/components/social-media/socialAccountVerificationDto";
import { UpdateConversationRequest } from "@/helpers/models/messenger/update-conversation-request.dto";
import { StartChatDTO } from "@/helpers/models/messenger/start-conversation.dto";
import { Message } from "@/helpers/models/messenger/message.model";
import { ApiRes } from "@/types/axios";
import { Portfolio } from "@/helpers/models/portfolio.model";
import utilService from "./util.service";
import keycloakConfig from "@/config/keycloak";
import authService, { AuthService } from "@/services/auth.service";
import { AccountBannedError } from "@/errors/AccountBannedError";
import { AccountDeletedError } from "@/errors/AccountDeletedError";
import { isAccountNotFoundApiError } from "@/helpers/accountDeletedSession";

class HttpService {
  async handleKeycloakCode(code: string) {
    try {
      const response = await api.post(`/auth/keyCloak/callback`, {
        code,
        redirect_uri: keycloakConfig.KEYCLOAK_REDIRECT_URL,
      });
      return response.data;
    } catch (error) {
      console.error("Error handling Keycloak code:", error);
      throw error;
    }
  }
  async logoutSession() {
    api.post(`/auth/logout`);

    if (!authService) {
      const authService = AuthService.getInstance();
      await authService.logout();
    }

    await authService.logout();
    return Promise.resolve();
  }
  async fetchNotificationCounts() {
    try {
      return api.get(
        `${utilService.getHostURLWithoutProtocol()}/notifications/notifications/counts`
      );
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  }

  public async fetchNotifications(page: number, limit: number, meta?: any) {
    try {
      const response = await api.get(
        `${utilService.getHostURLWithoutProtocol()}/notifications/notifications`,
        {
          params: { page, limit },
        }
      );

      // Log the full response to understand its structure
      console.log("API Response:", response);

      return response;
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  }

  public async getEventPortfoliosApi(
    eventId: string,
    name?: string,
    category?: string,
    status?: string
  ) {
    let queryParams: string[] = [];

    if (name !== undefined && name !== "") {
      queryParams.push(`name=${name}`);
    }
    if (category !== undefined) {
      queryParams.push(`category=${category}`);
    }
    if (status !== undefined) {
      queryParams.push(`status=${status}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

    const response = await api.get(
      `/event/allEventPortfolios/${eventId}${queryString}`
    );
    return response;
  }

  public async multiPortfolioStatusChangeApi(data) {
    const response = await api.patch(
      `/portfolio-v2/updateMultiplePortfoliosStatus`,
      data
    );
    return response.data;
  }

  public async portfolioStatusChangeApi(data) {
    const response = await api.patch(
      `/portfolio-v2/updatePortfolioStatus`,
      data
    );
    return response.data;
  }

  public async joinEventApi(eventId) {
    const response = await eventAPI.post(`/event/join/${eventId}`, {});
    return response.data;
  }

  public async subUnsubEventApi(eventId, action) {
    const response = await eventAPI.post(`/event/subscribe/${eventId}`, {
      action,
    });
    return response.data;
  }

  public async getEventParticipantsApi(
    eventId: string,
    name?: string,
    status?: string
  ) {
    let queryParams: string[] = [];

    if (name !== undefined && name !== "") {
      queryParams.push(`name=${name}`);
    }

    if (status !== undefined) {
      queryParams.push(`isRestrict=${status}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

    const response = await api.get(
      `/event/eventParticipant/${eventId}${queryString}`
    );
    return response;
  }

  public async getEventsApi(params = {}) {
    const response = await eventAPI.get(`/event/get`, {
      params,
    });

    return response;
  }

  public async getEventParticipantsApiV2(eventId, params = {}) {
    const response = await eventAPI.get(`/event/participant/${eventId}`, {
      params,
    });

    return response;
  }

  public async getEventTradeSummary(eventId, params = {}) {
    const response = await eventAPI.get(`/event/summary/${eventId}`, {
      params,
    });

    return response;
  }

  public async getEventLeaderboardApiV2(eventId, params = {}) {
    const response = await eventAPI.get(`/event/leaderboard/${eventId}`, {
      params,
    });

    return response;
  }

  public async getEventTopParticipant(eventId, params = {}) {
    const response = await eventAPI.get(`/event/topParticipant/${eventId}`, {
      params,
    });

    return response;
  }

  public async getEventDetailsApi(eventId) {
    const response = await eventAPI.get(`/event/detail/${eventId}`);
    return response;
  }

  public async updateParticipantStatus(eventId, data) {
    const response = await eventAPI.put(
      `/event/participant/${eventId}/update`,
      data
    );
    return response.data;
  }

  public async participantStatusChangeApi(data) {
    const response = await api.post(
      `/event/restrictOrReleaseSingleUserByPortfolio`,
      data
    );
    return response.data;
  }

  public async createEvent(data: any) {
    const response = await eventAPI.post(`/event/post`, data);
    return response.data;
  }

  public async uploadEventAssets(eventId: string, formData: FormData) {
    const response = await eventAPI.put(`/event/upload/${eventId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  public async getEventFiltersApi() {
    const response = await eventAPI.get(`/event/filters`);
    return response;
  }

  public async getEventGalleryApi(eventId: string) {
    const response = await eventAPI.get(`/event/gallery/get/${eventId}`);
    return response.data;
  }

  public async deleteEventGalleryImageApi(eventId: string, imageId: string) {
    const response = await eventAPI.delete(
      `/event/gallery/${eventId}/${imageId}/delete`
    );
    return response.data;
  }

  public async uploadEventGalleryImagesApi(
    eventId: string,
    formData: FormData
  ) {
    const response = await eventAPI.post(
      `/event/gallery/${eventId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  public async addPostToStockCollections(data: any) {
    const response = await api.post(`/posts/addtofeed`, data);
    return response.data;
  }
  public async markNotificationAsRead(ids: string[]) {
    const response = api.patch(
      `${utilService.getHostURLWithoutProtocol()}/notifications/notifications/read`,
      { ids }
    );
    return response;
  }

  public async markAllNotificationsAsRead() {
    const response = api.patch(
      `${utilService.getHostURLWithoutProtocol()}/notifications/notifications/read-all`
    );
    return response;
  }

  public async sendMessage(
    chatId: string,
    body: Message,
    optQuery: string = ""
  ) {
    const response = await api.post(
      `/conversation/message/${chatId}${optQuery}`,
      body
    );
    return response.data;
  }

  public async startChat(body: StartChatDTO) {
    const response = await api.post(`/conversation`, body);
    return response.data;
  }

  public async lastViewTime(body: any) {
    const response = await api.post(`/conversation/lastviewtime`, body);
    return response.data;
  }

  public async HistorySelectingStock(body: any) {
    const response = await api.post(`/stocks/select`, body);
    return response.data;
  }

  public async updateConnectionRequestStatus(
    conversationId: string,
    body: UpdateConversationRequest
  ) {
    const response = await api.put(
      `/conversation/request/${conversationId}`,
      body
    );
    return response.data;
  }

  public async getConversationById(conversationId: string) {
    const response = await api.get(`/conversation/${conversationId}`);
    return response.data;
  }

  public async muteNotification(conversationId: string) {
    const response = await api.put(
      `/conversation/notification/mute/${conversationId}`
    );
    return response.data;
  }

  public async unmuteNotification(conversationId: string) {
    const response = await api.put(
      `/conversation/notification/unmute/${conversationId}`
    );
    return response.data;
  }

  public async getSystemConfig() {
    try {
      const response = await api.get("/system-config");
      return response.data;
    } catch (e) {
      return;
    }
  }
  public async signUpForWaitlist(data: SignupWaitlistModel) {
    const response = await api.post("/waitlist", data);
    return response.data;
  }
  public async getWaitlistVerified(data: any) {
    const response = await api.get(`/waitlist/${data}`);
    return response.data;
  }
  public async signUp(data: SignupModel) {
    const response = await api.post("/auth/multiRegister", data);
    return response.data;
  }
  public async login(data: LoginModel) {
    const response = await api.post("/auth/multiLogin", data);
    return response.data;
  }
  public async gettwitterAuthUrlV1() {
    const response = await api.get("/twitter/v1/authUrl");
    return response.data;
  }
  public async handletwitterauth(data: any) {
    const response = await api.post("/twitter/v2/authenticate", data);
    return response.data;
  }

  public async handletwitterauthV1(data: any) {
    const response = await api.post("/twitter/v1/authenticate", data);
    return response.data;
  }

  public async sharePostOnTwitter(data: any) {
    const response = await api.post(`/profile/shareToTwitter`, data);
    return response.data;
  }

  public async resendVerificationLink(data: ResendVerificationDTO) {
    let response = await api.post(`/auth/resendVerificationEmail`, data);
    return response;
  }

  public async forgotPassword(data: ForgotPasswordModel) {
    const response = await api.post("/auth/forgotpassword", data);
    return response.data;
  }

  public async resetPassword(data: ResetPasswordModel) {
    const response = await api.post("/auth/resetpassword", data);
    return response.data;
  }

  public async verifyEmail(data: any) {
    const response = await api.post("/auth/verifyemail", data);
    return response.data;
  }

  public async verifyPhoneNumber(data: VerifyFirebaseToken) {
    const response = await api.post("/auth/verifyPhoneNumber", data);
    return response.data;
  }

  public async validateUsername(data: any) {
    const response = await api.post("/auth/validateusername", data);
    return response?.data;
  }

  /** Suggested usernames from full name (path segment must be encoded). */
  public async generateUsernamesFromFullName(fullName: string) {
    const segment = encodeURIComponent((fullName || "").trim());
    const response = await api.get(`/users/username/generate/${segment}`);
    return response.data;
  }

  // public async changePassword(data: ChangePasswordModel) {
  //   const response = await api.post("/auth/changepassword", data);
  //   return response.data;
  // }

  public async getUser(userName: string) {
    const response = await api.get(`/external/user/${userName}`);
    return response.data;
  }

  public async getUserById(userId: string) {
    const response = await api.get(`/users/byId/${userId}`);
    window.localStorage.setItem(
      "discord",
      JSON.stringify(response.data.data.discord)
    );
    return response.data;
  }

  public async updateUser(data: any) {
    const response = await api.put(`/users`, data);
    return response.data;
  }

  public async updateUserAfterConfiguration(data: any) {
    const response = await api.put(`/users/updateConfiguration`, data);
    return response.data;
  }

  public async getTopNasdaqStocks() {
    const response = await api.get(`/stocks/topNasdaq`);
    return response.data;
  }

  public async getSocialStocksCounter() {
    const response = await api.get(`/favourite-investment/counts`);
    return response.data;
  }

  public async getSocialStocks(query?: any) {
    const response = await api.get(`/favourite-investment?${query}`);
    return response.data;
  }

  public async getBlockUsers() {
    try {
      const response = await api.get(`/users/blockedusers`);
      return response.data;
    } catch (e) {
      return {};
    }
  }

  public async addSection(data: AddSectionDTO) {
    const response = await api.post(`/section`, data);
    return response.data;
  }

  public async updateSection(id: string, data: UpdateSectionDTO) {
    const response = await api.patch(`/section/${id}`, data);
    return response.data;
  }

  public async getAllSections(userId: string) {
    const response = await api.get(`/section/user/${userId}`);
    return response.data;
  }

  public async updateSectionOrders(data: any[]) {
    const response = await api.patch(`/section/all`, data);
    return response.data;
  }

  public async getQrCode(data: any) {
    const response = await api.post(`/qrcode/generate`, data);
    return response.data;
  }

  public async searchProfile(query: any) {
    const response = await api.get(`users/searchUser${query}`);
    return response;
  }

  public async recentSearchProfile() {
    const response = await api.get(`recent-searches`);
    return response;
  }

  public async deleteRecentSearchProfile(id: any) {
    const response = await api.delete(`/recent-searches/${id}`);
    return response;
  }

  public async updatingSettings(data: any) {
    const response = await api.patch(`/user-setting/widget`, data);
    return response;
  }

  public async getFilteredUser(query: any, options?: any) {
    const response = await api.get(`/leaderboard/filterUser${query}`, {
      signal: options?.signal,
    });
    return response;
  }

  public async getPublicFilteredUser(query: any, options?: any) {
    const response = await api.get(`/public/leaderboard/filterUser${query}`, {
      signal: options?.signal,
    });
    return response;
  }

  public async addSearchHistory(data: any) {
    const response = await api.post("/searchHistory", data);
    return response;
  }

  public async getSearchHistory() {
    const response = await api.get(`/searchHistory`);
    return response;
  }

  public async getFriendUser(query: any) {
    const response = await api.get(
      `/leaderboard/userfriends/${query}&flow=topFriends`
    );
    return response;
  }
  public async getPublicFriendUser(query: any) {
    const response = await api.get(`/public/users/userfriends/${query}`);
    return response;
  }

  public async getFollowedUser(query: any) {
    const response = await api.get(`/leaderboard/userFollowers/${query}`);
    return response;
  }

  public async getOtherUserFollowedUser(query: any, id: string) {
    const response = await api.get(`/leaderboard/userFollowers/${id}/${query}`);
    return response;
  }

  public async getFollowingUser(query: any) {
    const response = await api.get(`/leaderboard/userFollowing/${query}`);
    return response;
  }
  public async getOtherUserFollowingUser(query: any, id: string) {
    const response = await api.get(`/leaderboard/userFollowing/${id}/${query}`);
    return response;
  }

  public async getAllUserOfOtherUser(query: any, id: string) {
    const response = await api.get(`/leaderboard/myall/${id}/${query}`);
    return response;
  }

  public async getUserFollowAndFollowingCount() {
    const response = await api.get(`/users/connectionscount`);
    return response;
  }

  public async getFollowedInfluencers(data: any) {
    const response = await api.post(`/section/influencers/get`, data);
    return response;
  }
  public async getPublicFollowedInfluencers(data: any) {
    const response = await api.post(`/public/section/influencers/get`, data);
    return response;
  }

  public async unFollow(data: any) {
    const response = await api.post("/users/unfollow", data);
    return response;
  }

  public async addConnection(data: any) {
    const response = await api.post("/connections", data);
    return response;
  }

  public async cancelRequest(data: any) {
    const response = await api.post(`/connections/cancelrequest`, data);
    return response;
  }

  public async removeFriend(data: any) {
    const response = await api.post(`/connections/removefriend`, data);
    return response;
  }

  public async updateRequestConnection(
    connectionId: any,
    status: string,
    notificationId?: any
  ) {
    const response = await api.put(
      `/connections/${connectionId}/status/${status}`,
      { notificationId }
    );
    return response;
  }

  public async updateSocialLinks(data: any) {
    const response = await api.patch("/section/sociallinks", data);
    return response;
  }

  // FCM Notification

  public async registerFcm(payload: any): Promise<{ data: any }> {
    const response = api.post(
      `${utilService.getHostURLWithoutProtocol()}/notifications/fcm-tokens/register`,
      {
        ...payload,
      }
    );
    return response;
  }

  public async registerFCMNotification(deviceId: string, fcmToken: string) {
    const response = await api.patch("/eventnotification", {
      deviceId,
      fcmToken,
    });
    return response;
  }

  public async unregisterFCMNotification(deviceId: string, fcmToken: string) {
    const response = await api.put("/eventnotification/deleteNotification", {
      deviceId,
      fcmToken,
    });
    return response;
  }

  public async readNotification(notificationIds: string[]) {
    const response = await api.put("/eventnotification/readnotification", {
      ids: notificationIds,
    });
    return response;
  }

  public async readNotificationByType(sectionType: string) {
    const response = await api.put(
      `/eventnotification/readnotification/${sectionType}`
    );
    return response;
  }

  public async getNotificationSettings() {
    const response = await api.get("/notification-permission");
    return response;
  }
  public async notificationSettings(data: any) {
    const response = await api.put("/notification-permission", data);
    return response;
  }

  // End of FCM Notification

  public async getProfileData(username: string) {
    const response = await api.get(`/profile/${username}`);
    return response;
  }

  public async getSparklineData(
    stockId: string,
    startDate: number,
    endDate: number
  ) {
    const response = await api.get(
      `chart/sparkline/range?stockId=${stockId}&startDate=${startDate}&endDate=${endDate}`
    );
    return response.data;
  }

  // Wallpaper

  public async getWallpaperForUser() {
    const response = await api.get(`users/wallpapers`);
    return response.data;
  }

  public async addWallpaper(url: string) {
    const response = await api.post(`users/addWallpaper`, { url });
    return response.data;
  }

  public async updateWallpaper(websiteColor: string, websiteWallpaper: string) {
    const response = await api.patch(`users/updateWallpaper`, {
      websiteWallpaper,
      websiteColor,
    });
    return response.data;
  }

  // End of Wallpaper

  public async createWatchList(data: any) {
    const response = await api.post(`/watchlist`, data);
    return response.data;
  }

  public async updateWatchList(data: any) {
    const response = await api.put(`/watchlist`, data);
    return response.data;
  }

  public async deleteWatchList(data: any) {
    const response = await api.delete(`/watchlist`, data);
    return response.data;
  }

  public async getPublicWatchList(userId: any) {
    const response = await api.get(`/watchlist/user/${userId}`);
    return response;
  }

  public async deleteWatchlistByIds(ids: string[]) {
    const response = await api.post(`/watchlist/deleteMany`, ids);
    return response;
  }

  public async getAllWatchlistByToken() {
    const response = await api.get(`/watchlist`);
    return response.data;
  }

  public async getOHLCCardData(data: PredictionOHLC) {
    const response = await api.post(`/prediction/ohlc`, data);
    return response.data;
  }

  public async getPrivateWatchList() {
    const response = await api.get(`/watchlist`);
    return response;
  }
  public async deleteWatchlist(id: any) {
    const response = await api.delete(`/watchlist/removewatchlist/${id}`);
    return response;
  }
  public async updateWatchlistStatus(watchlistId: string, status: string) {
    const response = await api.put(
      `/watchlist/${watchlistId}/status/${status}`
    );
    return response;
  }
  public async updateMultipleWatchlistStatus(data: any) {
    const response = await api.post(`/watchlist/multiple/status`, {
      list: data,
    });
    return response.data;
  }

  public async getWatchlistByIds(watchlistIds: string[]) {
    const response = await api.post(`/watchlist/byIds`, watchlistIds);
    return response.data;
  }
  public async getPublicWatchlistByIds(watchlistIds: string[]) {
    const response = await api.post(`/public/watchlist/byIds`, watchlistIds);
    return response.data;
  }

  public async getSelection(data: any) {
    const response = await api.post(`/profile/saveSection`, data);
    return response.data;
  }

  public async createPlaybook(data: any) {
    const response = await api.post(`/playbook`, data);
    return response.data;
  }

  public async updatePlaybook(id: any, data: any) {
    const response = await api.patch(`/playbook/${id}`, data);
    return response.data;
  }

  public async updateMutiplePlaybook(data: any) {
    const response = await api.patch(`/playbook/multiple`, data);
    return response.data;
  }

  public async deletePlaybook(id: any) {
    const response = await api.delete(`/playbook/${id}`);
    return response;
  }

  public async deletePlaybookByStocks(data: any) {
    const response = await api.post(`/playbook/deletePlaybookByStocks`, data);
    return response;
  }

  public async getPredicationData(id: any) {
    const response = await api.post(`/prediction/fetch/${id}`);
    return response;
  }

  public async getPredictionAccuracies(userName: string) {
    const response = await api.get(
      `/profile/prediction-card?userName=${userName}`
    );
    return response.data;
  }

  public async getPublicPredictionAccuracies(userName: string) {
    const response = await api.get(
      `/public/profile/prediction-card?userName=${userName}`
    );
    return response.data;
  }

  public async getPredicationOhlcData(id: any) {
    const response = await api.post(`/prediction-ohlc/fetch/${id}`);
    return response;
  }

  public async getFavouriteInvestmentData(id: any, data: any) {
    const response = await api.patch(
      `/section/favouriteInvestment/${id}`,
      data
    );
    return response;
  }

  public async getDashboardFilterCountData() {
    const response = await api.get(`/leaderboard/diversificationCount`);
    return response;
  }

  public async getLeaderboardData(period: string) {
    const periodString = `period=${encodeURIComponent(period)}`;
    const response = await api.get(`/prediction-leaderboard?${periodString}`);
    return response.data;
  }

  public async socialSignup(data: any) {
    const response = await api.post(`/auth/sociallogin`, data);
    return response;
  }

  public async updateMutipleProfitAndLoss(data: any) {
    const response = await api.patch(`/profit-and-loss/multiple`, data);
    return response.data;
  }

  public async addProfitAndLoss(data: any) {
    const response = await api.post(`/profit-and-loss/trade`, data);
    return response.data;
  }

  public async updateProfitAndLoss(id: any, data: any) {
    const response = await api.patch(`/profit-and-loss/trade/${id}`, data);
    return response.data;
  }

  public async deleteProfitAndLoss(id: any) {
    const response = await api.delete(`/profit-and-loss/${id}`);
    return response.data;
  }

  public async getAllTradeForProfitAndLossByUserId() {
    const response = await api.get(
      `/profit-and-loss/ProfitAndLossDataForUserId`
    );
    return response.data;
  }
  public async getTradeByIds() {
    const response = await api.get(`/trades/tradeByIds`);
    return response.data;
  }

  public async addViewerWatchlist(id: any) {
    const response = await api.post(`/watchlist/addViewer/${id}`);
    return response.data;
  }

  public async reportUser(data: any) {
    const response = await api.put(`/users/reportUser`, data);
    return response.data;
  }

  public async getUngroupedPlaybook(userId: string) {
    const response = await api.get(`/playbook/ungrouped/${userId}`);
    return response.data;
  }

  public async getBestTradeByUserId(data: any) {
    // const response = await api.put(`profit-and-loss/profitAndLossByIds`, data);
    const response = await api.get(`trades/tradeByIds`, data);
    return response;
  }
  public async getUserSelectedBestTrade(data: any) {
    const response = await api.post(`trades/gettrades`, {
      ids: data,
    });
    return response;
  }

  public async addPredictionOhlc(data: any) {
    const response = await api.post(`/prediction-ohlc`, data);
    return response.data;
  }

  public async addPrediction(data: any) {
    const response = await api.post(`/prediction`, data);
    return response.data;
  }

  public async deletePredictionOHLC(id: any) {
    const response = await api.delete(`/prediction-ohlc/${id}`);
    return response.data;
  }

  public async deletePrediction(id: any) {
    const response = await api.delete(`/prediction/${id}`);
    return response.data;
  }

  public async getAllPredictionsByUserId() {
    const response = await api.get(`/prediction/all`);
    return response.data;
  }

  public async getAllSectionByType(type: any) {
    const response = await api.get(`/section/type/${type}`);
    return response.data;
  }

  public async getAllStocksHighlights(stockId: any) {
    const response = await api.get(`/stocks/highlights/${stockId}`);
    return response.data;
  }

  public async updatePredictionOhlc(id: any, data: any) {
    const response = await api.patch(`/prediction-ohlc/${id}`, data);
    return response.data;
  }

  public async updatePrediction(id: any, data: any) {
    const response = await api.patch(`/prediction/${id}`, data);
    return response.data;
  }

  public async updateMutiplePrediction(data: any) {
    const response = await api.patch(`/prediction/multiple`, data);
    return response.data;
  }

  public async updateMutiplePredictionOhlc(data: any) {
    const response = await api.patch(`/prediction-ohlc/multiple`, data);
    return response.data;
  }

  public async deleteUserAccount(data: { currentPassword: string }) {
    const response = await api.patch(`/users/delete`, data);
    return response.data;
  }

  public async recreateAccount() {
    const response = await api.post(`/users/recreateAccount`);
    return response.data;
  }

  public async changePassword(data: any) {
    const response = await api.post(`/users/changepassword`, data);
    return response.data;
  }

  public async getLinkedInAuthUrl() {
    const response = await api.get("/linkdin-autherization");
    return response.data;
  }
  public async handleLinkedInAuth(data: any) {
    const response = await api.post(
      "/linkdin-autherization/authenticateWithLinkdin",
      data
    );
    return response.data;
  }

  public async getRedditAuthUrl() {
    const response = await api.get("/reddit/authUrl");
    return response.data;
  }

  public async handleRedditAuth(queryParams: any) {
    // Extract code and state from the queryParams object
    const code = queryParams.code;
    const state = queryParams.state;
    // Construct the query string
    const queryString = `code=${encodeURIComponent(
      code
    )}&state=${encodeURIComponent(state)}`;
    // Make the GET request to the server with the query string appended to the URL
    const response = await api.get(
      `/reddit/authorizeWithReddit?${queryString}`
    );
    return response.data;
  }

  public async sharePostOnSocial(data: any) {
    const response = await api.post(`/profile/external-share`, data);
    return response.data;
  }

  public async updateUserProfile(data: any) {
    const response = await api.put(`/users/updateprofile`, data);
    return response.data;
  }

  public async getEmailVerificationCode(email: string) {
    const response = await api.put(
      `/users/emailVerificationCode?email=${email}`
    );
    return response.data;
  }

  public async EmailVerificationPosting(data: any) {
    const response = await api.post(`/users/verifyEmail`, data);
    return response.data;
  }

  public async twoFactorAuthentication() {
    const response = await api.get("/two-factor-authentication");
    return response.data;
  }
  public async twoFactorAuthenticationPost(data: any) {
    const response = await api.post("/two-factor-authentication", data);
    return response.data;
  }

  public async socialAuthentication(data: any) {
    const response = await api.patch(`/users/toggleSocialAuthentication`, data);
    return response.data;
  }
  public async deleteTwoFaAuth(id: any) {
    const response = await api.delete(`/two-factor-authentication/${id}`);
    return response;
  }
  public async updateTwoFaAuth(id: any) {
    const response = await api.patch(`/two-factor-authentication/${id}`);
    return response;
  }
  public async twoFactorAuthenticationVerifyPhone(data: any) {
    const response = await api.post(
      "/two-factor-authentication/verifyPhone/v2",
      data
    );
    return response.data;
  }
  public async updateTwoFaAuthStatus() {
    const response = await api.patch(`two-factor-authentication/toggle`);
    return response;
  }
  public async verifyCurrentPassword(data: any) {
    const response = await api.patch(`/users/verifyChangePassword`, data);
    return response.data;
  }

  public async getComparisonData(symbol: string, ticker2: string) {
    const response = await api.get(
      `/plot_price_comparison?ticker_1=${symbol}&ticker_2=${ticker2}&days_back=365`
    );
    return response;
  }

  public async getPricePerformance(stockId: string) {
    const response = await api.get(`/stockdetail/priceperformance/${stockId}`);
    return response;
  }
  public async getShareStats(stockId: string) {
    const response = await api.get(`/stockdetail/shareStats/${stockId}`);
    return response;
  }
  public async getFundamentals(stockId: string) {
    const response = await api.get(`/stockdetail/fundamentals/${stockId}`);
    return response;
  }
  public async getRelatedNews(stockId: string) {
    const response = await api.get(`/stockdetail/relatedNews/${stockId}`);
    return response;
  }
  public async getPrediction(data: any) {
    const response = await api.post(`/stockdetail/prediction`, data);
    return response;
  }
  // public async getRating(stockId: string) {
  //   const response = await api.get(`/stockdetail/rating/${stockId}`);
  //   return response;
  // }

  public async getTechnicalIndicator(data: any) {
    const response = await api.post(`/stockdetail/technicalIndicator`, data);
    return response;
  }
  public async searchStockBySymbol(keyword: string) {
    const response = await api.get(`/stocks/search/symbols?keyword=${keyword}`);
    return response;
  }

  public async getStockNews(symbol: string, market: string) {
    const response = await api.get(
      `news/stock?symbol=${symbol}&market=${market}`
    );
    return response;
  }

  public async updatePlaybookV2(id: any, data: any) {
    const response = await api.patch(`/playbook-v2/${id}`, data);
    return response.data;
  }

  public async createPlaybookV2(data: any) {
    const response = await api.post(`/playbook-v2`, data);
    return response.data;
  }

  public async deletePlaybookV2(id: any) {
    const response = await api.delete(`/playbook-v2/${id}`);
    return response;
  }

  public async getAllPlaybookByUserId() {
    const response = await api.get(`/playbook-v2/playbookForDataUserId`);
    return response.data;
  }

  public async updateMutiplePlaybookV2(data: any) {
    const response = await api.patch(`/playbook-v2/multiple`, data);
    return response.data;
  }
  public async updateMutipleTrades(data: any) {
    const response = await api.patch(`/trades/multiple`, data);
    return response.data;
  }

  public async fetchMetaImage(url: string) {
    const response = await api.post(`/meta-validator/fetchImage`, { url });
    return response.data;
  }

  public async getMetaTitle(data: any) {
    const response = await api.post(`/meta-validator`, data);
    return response.data;
  }

  // Feed Work //

  public async getURlMetaData(url: string) {
    const response = await api.post(`/posts/metadata`, { url: url });
    return response;
  }

  public async getUsers(keyword: string) {
    const response = await api.get("/users?keyword=" + keyword);
    return response;
  }

  public async searchStockCrypto(keyword: string = "", limit: number = -1) {
    var query = "?";
    if (keyword) {
      query += `keyword=${keyword}`;
    }
    if (limit != -1) {
      query += `&limit=${limit}`;
    }
    let response = await api.get("/cache-service/search/all" + query);
    return response;
  }

  public async searchCryptoSearch(
    type: any,
    keyword: string = "",
    limit: number = -1
  ) {
    var query = "?";
    if (keyword) {
      query += `keyword=${keyword}`;
    }
    if (limit != -1) {
      query += `&limit=${limit}`;
    }
    let response = await api.get(`/cache-service/search/${type}` + query);
    return response;
  }

  public async searchStockSymbol(keyword: string = "") {
    var query = "?";
    if (keyword) {
      query += `keyword=${keyword}`;
    }
    let response = await api.get(`/stocks/search/symbols?keyword=${keyword}`);
    return response;
  }

  public async searchStockSearch(
    type: any,
    keyword: string = "",
    limit: number = -1
  ) {
    var query = "?";
    if (keyword) {
      query += `keyword=${keyword}`;
    }
    if (limit != -1) {
      query += `&limit=${limit}`;
    }
    let response = await api.get(`/cache-service/search/${type}` + query);
    return response;
  }

  public async addPost(data: PostsModel) {
    const response = await api.post(`/posts`, data);
    return response;
  }

  public async updatePost(data: UpdatePostModel) {
    const response = await api.put(`/posts`, data);
    return response;
  }
  public async uploadPdf(data: File | FormData) {
    const response = await api.post(`/posts/pdf`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response;
  }

  public async deletePost(paylaod: any) {
    const response = await api.delete(`/posts`, { data: paylaod });
    return response;
  }

  public async getPublicPostById(postId: any) {
    const response = await api.get(`/external/post/${postId}`);
    return response.data;
  }

  public async getPublicPostByIdWithoutToken(postId: any) {
    const response = await api.get(`/public?postId=${postId}`);
    return response.data;
  }

  public async votePollStock(data: any) {
    const response = await api.post(`/posts/voteonpollchart`, data);
    return response;
  }

  public async getTopCrypto() {
    const response = await api.get(`/crypto/top`);
    return response;
  }

  public async addComment(data: CommentsModel) {
    const response = await api.post("/posts/comment", data);
    return response;
  }

  public async removeComment(id: string) {
    const response = await api.delete(`/posts/comment/${id}`);
    return response;
  }

  public async searchStock(keyword: string = "", limit: number = -1) {
    var query = "?";
    if (keyword) {
      query += `keyword=${keyword}`;
    }
    if (limit != -1) {
      query += `&limit=${limit}`;
    }
    let response = await cacheApi.get("/search/stock" + query);
    return response;
  }

  public async searchCrypto(keyword: string = "", limit: number = -1) {
    var query = "?";
    if (keyword) {
      query += `keyword=${keyword}`;
    }
    if (limit != -1) {
      query += `&limit=${limit}`;
    }
    let response = await cacheApi.get("/search/crypto" + query);
    return response;
  }

  public async searchStockForPost(keyword: string = "", limit: number = -1) {
    var query = "?";
    if (keyword) {
      query += `keyword=${keyword}`;
    }
    if (limit != -1) {
      query += `&limit=${limit}`;
    }
    let response = await api.get("/cache-service/search/stock" + query);
    return response;
  }

  public async searchCryptoForPost(keyword: string = "", limit: number = -1) {
    var query = "?";
    if (keyword) {
      query += `keyword=${keyword}`;
    }
    if (limit != -1) {
      query += `&limit=${limit}`;
    }
    let response = await api.get("/cache-service/search/crypto" + query);
    return response;
  }

  public async getData(url: string) {
    const response = await api.get(url);
    return response;
  }

  public async handleDataForAssets(data: any) {
    let response = await api.post(`/savedassets`, data);
    return response;
  }

  public async deleteDataFromAssets(id: any) {
    let response = await api.delete(`/savedassets?assetId=${id}`, {});
    return response;
  }

  public async getSavedAssets(filterType: any) {
    let response = await api.get(`/savedassets?filterType=${filterType}`);
    return response;
  }

  public async likePost(likePostModel: LikePostModel) {
    const response = await api.post(`/posts/like`, likePostModel);
    return response;
  }

  public async unlikePost(unlikePostModel: UnlikeModel) {
    const response = await api.post(`/posts/unlike`, unlikePostModel);
    return response;
  }

  public async getStocks() {
    const response = await api.get(`/stocks`);
    return response;
  }

  public async getCryptos(apiUrl: any) {
    let response = await api.get(`/crypto/header?symbol=${apiUrl}`);
    return response;
  }

  public async getstocks(symbols: string) {
    let queryparams = `symbol=${symbols}&column=all`;
    const response = await api.get(`/stocks/live?${queryparams}`);
    return response;
  }
  public async submitBugReport(data: any) {
    let response = await api.post(`/reported-bug`, data);
    return response;
  }
  public async addEtradeAccount(accountId: string) {
    const response = await api.post(`/etrade/portfolio/${accountId}`);
    return response;
  }
  public async getEtradeAccountBalance(accountId: string) {
    const response = await api.get(
      `/etrade/account/balance?accountId=${accountId}`
    );
    return response;
  }
  public async authenticateEtrade(oAuthToken: string, oAuthVerifier: string) {
    const response = await api.post(`/etrade/authenticate`, {
      oAuthToken,
      oAuthVerifier,
    });
    return response;
  }
  public async GetPortgoliosTransactionListing(id: string) {
    let response = await api.get(`/portfolio/transactions/${id}`);
    return response;
  }
  public async deletePortfolio(id: string) {
    let response = await api.delete(`/portfolio?portfolioId=${id}`);
    return response;
  }
  public async GetPortfolioSellItems(id: string) {
    let response = await api.get(`/portfolio/sellItems/${id}`);
    return response;
  }
  // public async AddPortfolio(portfolio: any) {
  //   let response = await api.post(`/portfolio`, portfolio);
  //   return response;
  // }

  public async AddPortfolio(portfolio: any) {
    let response = await api.post(`/portfolio-v2`, portfolio);
    return response;
  }

  public async updatePortfolio(portfolioId: string, updateBody: any) {
    let response = await api.patch(`/portfolio/${portfolioId}`, updateBody);
    return response;
  }
  public async AddItemsToPortfolio(dataArray: any) {
    let response = await api.post(`/portfolio/addUpdateItems`, dataArray);
    return response;
  }
  public async getPurchasedPortfolios(userID: string) {
    let response = await api.get(`/portfolio/purchased/${userID}`);
    return response;
  }
  public async getPortfoliosListingByUserId(userId: string) {
    let response = await api.get(`/portfolio/byUser/${userId}`);
    return response;
  }
  public async GetPortoliosHoldingsListing(id: string) {
    let response = await api.get(`/portfolio/holding/${id}`);
    return response;
  }
  public async GetPortoliosSummaryListing(id: string) {
    let response = await api.get(`/portfolio/summary/${id}`);
    return response;
  }
  public async getPortfolioSummaryById(id: string) {
    let response = await api.get(`/portfolio-trade/?portfolio_id=${id}`);
    return response;
  }
  public async getPortfolioSummaryByIdv2(portfolioId: string) {
    let response = await api.get(`/portfolio-v2/summary-trades/${portfolioId}`);
    return response;
  }

  public async getsnaptradeSummaryApi(accountId: string) {
    let response = await api.get(`/snaptrade/orderSummery/${accountId}`);
    return response;
  }
  // public async GetPortgoliosListing() {
  //   let response = await api.get(`/portfolio`);
  //   return response;
  // }
  public async GetPortgoliosListing(offset?: number, events?: boolean) {
    let offsetValue = offset ? offset : 0;
    let response = await api.get(
      `/portfolio-v2?offset=${offsetValue}&event=${events}`
    );
    // let response = await api.get(`/portfolio-v2`);
    return response;
  }

  public async GetPortfolioDetail(id: string) {
    let response = await api.get(`/portfolio-v2/detail/${id}`);
    return response as ApiRes<Portfolio>;
  }

  public async GetPortfoliosListingV2() {
    let response = await api.get(`/portfolio-v2/allPortfolio`);
    return response;
  }

  public async getEtradeAuthURL() {
    const response = await api.get(`/etrade/authUrl`);
    return response;
  }
  public async getWatchList() {
    const response = await api.get("/watchlist");
    return response;
  }
  public async loadWatchlist(watchlistId: string) {
    let repsonse = await api.post(`/watchlist/load`, {
      watchlistId: watchlistId,
    });
    return repsonse;
  }
  public async snapTradeConnection(slug) {
    const response = await api.get("/snaptrade/connectionLink?broker=" + slug);
    return response.data;
  }
  public async loadBrokerageAuthorizations() {
    const response = await api.get("/snaptrade/brokerageAuthorizations");
    return response.data;
  }

  public async getSnaptradeAccounts() {
    const response = await api.get("/snaptrade/accounts");
    return response.data;
  }

  public async getExchangeList() {
    const response = await api.get("/snaptrade/exchangeList");
    return response.data;
  }
  public async loadsnapTradeAccounts(id: number) {
    const response = await api.get(
      `/snaptrade/accounts?brokerageAuthorizationId=${id}`
    );
    return response;
  }
  public async loadSnapTradePortfolio(id: any) {
    let response = await api.post(`/snaptrade/portfolio/${id}`);
    return response;
  }

  public async brokerageList() {
    const response = await api.get(`/snaptrade/brokerageList`);
    return response;
  }

  public async snaptradeHoldings(accountId, type = "all") {
    const response = await api.get(
      `/snaptrade/holdings/${accountId}?filterType=${type}`
    );
    return response;
  }

  public async brokerageSymbols(accountId, symbol) {
    const response = await api.get(
      `/snaptrade/brokerage_search_symbol?accountId=${accountId}&symbol=${symbol}`
    );
    return response;
  }

  public async brokerageTickerDetails(accountId, symbolId) {
    const response = await api.get(
      `/snaptrade/get_ticker?accountId=${accountId}&symbol_id=${symbolId}`
    );
    return response;
  }

  public async brokerageCryptoOrderApi(payload: any) {
    const response = await api.post(`/snaptrade/place_crypto_order`, payload);
    return response;
  }

  public async brokeragePlaceOrderV2Api(payload: any) {
    const response = await api.post(`/snaptrade/place_order_v2`, payload);
    return response;
  }

  public async deleteBrokerageAccountApi(id) {
    const response = await api.delete(
      `/snaptrade/brokerageAuthorization/${id}`
    );
    return response;
  }

  public async getSystemConfiguration(keyword: string) {
    try {
      const response = await api.get(`/system-config?type=${keyword}`);
      return response.data;
    } catch (e) {
      return;
    }
  }

  public async updateConnectionType(connectionId: any, relationType: any) {
    const response = await api.patch(`/connections/relation/${connectionId}`, {
      relationType: relationType,
    });
    return response;
  }

  public async getByUsername(userName: string) {
    const response = await api.get(`/users/${userName}/username`);
    return response;
  }

  public async getTopFriends(userId: string) {
    const response = await api.get(`/topfriends/${userId}`);
    return response;
  }

  public async getConnections(connectionType: string, userId?: string) {
    const response = await api.get(
      `/connections?connectionType=${connectionType}&userId=${userId}`
    );
    return response;
  }

  public async getWatchListByUserId(userId: string) {
    const response = await api.get("/watchlist/user/" + userId);
    return response;
  }

  public async getConnectionFriendReqStatus(profileId: string) {
    const response = await api.get(
      `/connections/connectionStatus/${profileId}`
    );
    return response;
  }

  public async getpublicPapers(userId: any) {
    let response = await api.get(`/papers/public/${userId}`);
    return response;
  }

  public async getConnectionsCount(userId: string) {
    const response = await api.get("/connections/count?userId=" + userId);
    return response;
  }

  public async authenticatedUserFriends(data: any) {
    const response = await api.post(`/connections/authenticated`, data);
    return response;
  }

  public async getDictionaryBykeyword(keyword: string) {
    const response = await api.get(`/dictionary/search/${keyword}`);
    return response.data;
  }

  public async searchTopFriends(userId: string, keyword: string) {
    const response = await api.get(
      `/topfriends/${userId}/search?keyword=${keyword}`
    );
    return response;
  }

  public async addTopFriend(topFriend: AddTopFriendDTO) {
    const response = await api.post(`/topfriends`, topFriend);
    return response;
  }
  public async checkIfFollowStockExist(data: any) {
    let repsonse = await api.post(`/stocks/checkifexist`, data);
    return repsonse;
  }
  public async unfollowStocks(data: any) {
    let repsonse = await api.post(`/stocks/unfollow`, data);
    return repsonse;
  }
  public async followStocks(data: FollowStockModel) {
    let repsonse = await api.post(`/stocks/follow`, data);
    return repsonse;
  }

  public async deleteTopFriend(topFriend: AddTopFriendDTO) {
    const response = await api.delete(`/topfriends`, { data: topFriend });
    return response;
  }

  public async filterUserSearch(keyword: string, type: string = "all") {
    const response = await api.get(
      `/leaderboard/filterUser?type=${type}&page=1&count=10&keyword=${keyword}`
    );
    return response;
  }

  public async publicfilterUserSearch(type: string = "all") {
    const response = await api.get(
      `/leaderboard/filterUser?type=${type}&page=1&count=10`
    );
    return response;
  }

  public async getReportedBugs(page: string = "1", pageSize: string = "10") {
    var query = "?";
    if (page) {
      query += `page=${page}`;
    }
    if (pageSize) {
      query += `&pageSize=${pageSize}`;
    }
    return api.get(`/reported-bug${query}`);
  }

  public async socialAccountVerification(data: SocialAccountVerificationDTO) {
    const response = await api.post(`/social-accounts-verification`, data);
    return response;
  }

  public async logoutUser() {
    const response = await api.post("/users/logout");
    return response.data;
  }

  public async blockUser(data: any) {
    const response = await api.post(`/users/block`, data);
    return response;
  }

  public async unBlockUser(data: any) {
    const response = await api.post(`/users/unblock`, data);
    return response;
  }

  public async getProfileRelations() {
    const response = await api.get(`/profile/relation`);
    return response;
  }

  // public async getfinancialChart(stockId: string) {
  //   const response = await api.get(`/stockdetail/financialChart/${stockId}`);
  //   return response;
  // }
  // public async getMainguage(stockId: string) {
  //   const response = await api.get(`/stockdetail/mainguages/${stockId}`);
  //   return response;
  // }
  public async getAllguages(stockId: string) {
    const response = await api.get(`/stockdetail/allguages/${stockId}`);
    return response;
  }
  public async getCompanyDetails(stockId: string) {
    const response = await api.get(`/stockdetail/companyDetails/${stockId}`);
    return response;
  }

  public async getInsiderTrades(stockId: string) {
    const response = await api.get(`/stockdetail/insiderTrades/${stockId}`);
    return response;
  }

  public async getPlaidConnectionLink() {
    return await api.get(`/plaid/connectionLink`);
  }
  public async addPlaidBrokerageData(body: any) {
    return await api.post(`/plaid/brokerageAuthorization`, body);
  }
  public async getPlaidBrokerageData() {
    return await api.get(`/plaid/brokerageAuthorizations`);
  }

  public async addPlaidPortfolio(brokerageId: string) {
    return await api.post(`/plaid/portfolio/${brokerageId}`);
  }

  public async getUserPhotos(userId: any) {
    return await api.get(`/users/photos/${userId}`);
  }

  public async getPublicUserPhotos(userId: any) {
    return await api.get(`/public/user/photos/${userId}`);
  }

  public async getCompanies() {
    const response = await api.get("companies");
    return response;
  }

  public async addwidgetconfiguration(widget: any) {
    // GET filters by pageTitle=dashboard; always persist the same key so
    // widgets survive a reload.
    const payload = {
      ...widget,
      pageTitle: widget?.pageTitle || "dashboard",
    };
    const response = await api.post("/widgetconfiguration", payload);
    return response;
  }

  public async getLeaderboard(query: any) {
    const response = await api.get(`/leaderboard/leaderboard${query}`);
    return response;
  }

  public async getLeaderboardTrade(query: any) {
    const response = await api.get(`/leaderboard/trade${query}`);
    return response;
  }

  public async getLeaderboardWidget(query: any) {
    const response = await api.get(`/leaderboard${query}`);
    return response;
  }

  public async getLeaderboardWidgetTrades(query: any) {
    const response = await api.get(
      `/leaderboard/tradeLeaderboardWidgets${query}`
    );
    return response;
  }

  public async addPostsTrade(data: any) {
    const response = await api.post(`/posts/trade`, data);
    return response.data;
  }

  public async addPredictionTimeline(data: any) {
    const response = await api.post(`/prediction/predictiontimeline`, data);
    return response.data;
  }

  public async tradeLivePrice(data: any) {
    const response = await api.post(`/posts/liveprice`, data);
    return response.data;
  }

  public async searchSubscriptions(keyword: string) {
    const response = await api.get(
      `/portfolio/subscriptions?keyword=${keyword}`
    );
    return response.data;
  }

  public async searchConnectionByType(connectionType: string, keyword: string) {
    const response = await api.get(
      `/connections/search/${connectionType}?keyword=${keyword}`
    );
    return response;
  }

  public async getWidgetsConfig() {
    const response = await api.get(`/widgetconfiguration?pageTitle=dashboard`);
    return response;
  }

  public async getAllStocksFilterCount(filter: any) {
    const response = await api.get(`/stocks/stocksCount?type=${filter}`);
    return response.data;
  }

  public async getAllStocksWithFilter(query: any) {
    const response = await api.get(`/stocks/type${query}`);
    return response.data;
  }

  public async getAllStocksCountFilter() {
    const response = await api.get(`/favourite-investment/counts`);
    return response.data;
  }

  public async getEquityShares(portfolioId) {
    const response = await api.get(`/portfolio/equity-shares/${portfolioId}`);
    return response.data;
  }

  public async getStats(portfolioId) {
    const response = await api.get(`/portfolio/stats/${portfolioId}`);
    return response.data;
  }

  public async getStatsBrokerage(accountId) {
    const response = await api.get(`/snaptrade/get_stats/${accountId}`);
    return response.data;
  }

  public async getOverAllInvestment(accountId) {
    const response = await api.get(
      `/snaptrade/overall_investment/${accountId}`
    );
    return response.data;
  }

  public async getRankings(portfolioId) {
    const response = await api.get(`/portfolio/rankings/${portfolioId}`);
    return response.data;
  }

  public async getRankingsBrokerage(accountId) {
    const response = await api.get(`/snaptrade/get_rankings/${accountId}`);
    return response.data;
  }

  public async getDistributions(portfolioId: string) {
    const response = await api.get(`/portfolio/distributions/${portfolioId}`);
    return response.data;
  }

  public async getDistributionsBrokerage(accountId) {
    const response = await api.get(`/snaptrade/get_distributions/${accountId}`);
    return response.data;
  }

  public async cummulativeReturn(portfolioId) {
    const response = await api.get(
      `/portfolio/cummulative-return/${portfolioId}`
    );
    return response;
  }

  public async portfolioHoldings(portfolioId) {
    const response = await api.get(
      `/portfolio-v2/holdings?portfolioId=${portfolioId}`
    );
    return response.data;
  }

  public async cummulativeReturnBrokerage(accountId) {
    const response = await api.get(
      `/snaptrade/get_cummulative_return/${accountId}`
    );
    return response;
  }

  public async getProfileStats(portfolioId) {
    const response = await api.get(
      `/portfolio/performanceStats/${portfolioId}`
    );
    return response.data;
  }

  public async getProfileStatsBrokerage(portfolioId) {
    const response = await api.get(
      `/snaptrade/get_performance_stats/${portfolioId}`
    );
    return response.data;
  }
  public async weightedReturn(portfolioId) {
    const response = await api.get(`/portfolio/weightedReturn/${portfolioId}`);
    return response.data.data;
  }
  public async weightedReturnBrokerage(portfolioId) {
    const response = await api.get(`/snaptrade/weightedReturn/${portfolioId}`);
    return response.data.data;
  }

  public async heatMap(portfolioId) {
    const response = await api.get(`/portfolio/heatMap/${portfolioId}`);
    return response.data.data;
  }

  public async symbolGet(symbol) {
    const response = await api.get(`/stocks/symbolInfo?symbol=${symbol}`);
    return response.data.data;
  }

  public async createMarketTrade(data: any) {
    const response = await api.post("/portfolio-trade/create/market", data);
    return response.data;
  }

  public async createLimitTrade(data: any) {
    const response = await api.post("/portfolio-trade/create/limit", data);
    return response.data;
  }

  public async etfChart(portfolioId) {
    const response = await api.get(
      `/portfolio/compositionChart/${portfolioId}`
    );
    return response.data.data;
  }
  public async etfChartBrokrage(portfolioId) {
    const response = await api.get(
      `/snaptrade/compositionChart/${portfolioId}`
    );
    return response.data.data;
  }

  public async getPortfolioOrdersTable(portfolioId: string, status: string) {
    const response = await api.get("/portfolio-trade", {
      params: {
        portfolio_id: portfolioId,
        status: status,
      },
    });
    return response.data;
  }

  public async getPortfolioOrdersTableAll(portfolioId: string) {
    const response = await api.get("/portfolio-trade", {
      params: {
        portfolio_id: portfolioId,
      },
    });
    return response.data;
  }

  public async getPortfolioTransactionsTable(portfolioId) {
    const response = await api.get(`/portfolio/allTransactions/${portfolioId}`);
    return response.data.data;
  }

  public async getTicketDetail(ticketToken: string, email: string) {
    const response = await api.get(`/ticket`, {
      params: { ticketToken, email },
    });
    return response.data;
  }

  public async markViewed() {
    const response = await api.post(`/connections/markviewed`);
    return response;
  }

  public async updateConnection(connectionId: string, status: string) {
    const response = await api.put(
      `/connections/${connectionId}/status/${status}`
    );
    return response;
  }

  public async getCurrentUser() {
    try {
      const response = await api.get(`/users/me`);
      return response.data;
    } catch (e: any) {
      if (e?.response?.status === 403) {
        throw new AccountBannedError();
      }
      if (isAccountNotFoundApiError(e)) {
        throw new AccountDeletedError();
      }
      throw e;
    }
  }
  public async postTradeFromProfileScreen(data: any) {
    let response = await api.post(`/trades`, data);
    return response;
  }
  public async getLeaderboardDataForTrades(query: any) {
    const response = await api.get(`/leaderboard/tradeLeaderboard${query}`);
    return response;
  }

  public async getFilteredUserCount(userId: any) {
    const response = await api.get(`/leaderboard/counts?userId=${userId}`);
    return response;
  }

  public async getStockChartData(stockIds: string[]) {
    const stockIdsParam = stockIds.join(",");
    const response = await api.get(`/stocks/chart?stockIds=${stockIdsParam}`);
    return response.data;
  }

  public async getPublicStockChartData(stockIds: string[]) {
    const stockIdsParam = stockIds.join(",");
    const response = await api.get(
      `/public/stocks/chart?stockIds=${stockIdsParam}`
    );
    return response.data;
  }

  public async getCircles(page: number = 1, count: number = 10) {
    const response = await api.get(`/circles`, {
      params: { page, count },
    });
    return response.data;
  }

  public async deleteCircle(circleId: string) {
    const response = await api.delete(`/circles/${circleId}`, {
      data: { circleId },
    });
    return response.data;
  }

  public async validateCircleName(name: string) {
    const response = await api.get(
      `/circles/validate/${encodeURIComponent(name)}`
    );
    return response.data;
  }

  public async getCircleConnections(
    circleId: string,
    page: number = 1,
    count: number = 10
  ) {
    const response = await api.get(`/circles/connections`, {
      params: { id: circleId, page, count },
    });
    return response.data;
  }

  public async addCircleMember(circleId: string, memberIds: string[]) {
    const response = await api.post(`/circles/${circleId}/addmember`, {
      memberIds,
    });
    return response.data;
  }

  public async removeCircleMember(circleId: string, memberIds: string[]) {
    const response = await api.put(`/circles/${circleId}/removemember`, {
      memberIds,
    });
    return response.data;
  }

  public async createCircle(data: {
    name: string;
    hexColor: string;
    memberIds: string[];
  }) {
    const response = await api.post(`/circles`, data);
    return response.data;
  }

  public async updateCircle(
    circleId: string,
    data: { name: string; hexColor: string }
  ) {
    const response = await api.put(`/circles/${circleId}`, data);
    return response.data;
  }

  public async getSignalsSummaryTrades(name) {
    const response = await api.get(`/signals/summary-trades/${name}`);
    return response.data.data;
  }
  public async getDetailedReactions(
    sourceId: string,
    params?: { page?: number; count?: number; type?: string }
  ) {
    const queryParams = new URLSearchParams();
    if (params?.page != null) {
      queryParams.append("page", String(params.page));
    }
    if (params?.count != null) {
      queryParams.append("count", String(params.count));
    }
    if (params?.type) {
      queryParams.append("type", params.type);
    }
    const query = queryParams.toString();
    const url = `/posts/reactions/${sourceId}${query ? `?${query}` : ""}`;
    const response = await api.get(url);
    return response.data.data;
  }

  public async getCurrentpnl(portfolioId, symbol) {
    const response = await api.get(
      `/portfolio-v2/current-pnl?portfolioId=${portfolioId}&symbol=${symbol}`
    );
    return response.data;
  }

  public async getRatingsByStockId(stockId: string) {
    const response = await api.get(
      `/stocks/getAssetRatingByStockId?stockId=${encodeURIComponent(stockId)}`
    );
    return response.data;
  }

  public async handleValidity(platform, username) {
    const response = await api.get(
      `/shareability/verify-username?platform=${platform}&username=${username}`
    );
    return response.data;
  }

  public async redirectToLink(platform: any) {
    const response = await api.get(`/shareability/${platform}/redirect-url`);
    return response.data;
  }

  public async handleDiscordConnectivity(data: any) {
    const response = await api.post(`/shareability/discord/user-info`, data);
    return response.data;
  }

  public async handleFacebookConnectivity(data: any) {
    const response = await api.post(`/shareability/facebook/user-info`, data);
    return response.data;
  }

  public async handleDiscordPosting(data: any) {
    const response = await api.get(
      `/shareability/discord/guild-channels?guilds=${encodeURIComponent(
        JSON.stringify(data)
      )}`
    );
    return response.data;
  }

  public async fetchTelegramUserData(encodedData: any) {
    const response = await api.get(
      `/shareability/telegram/user-info?code=${encodedData}`
    );
    return response.data;
  }

  public async handleXCallback(data: any) {
    const response = await api.post(`/shareability/x/user-info`, data);
    return response.data;
  }

  public async otherPlatforms(platform: any, data: any) {
    const response = await api.post(
      `/shareability/${platform}/user-info`,
      data
    );
    return response.data;
  }

  public async XotherPlatforms(data: any) {
    const response = await api.post(`/shareability/x/user-info`, data);
    return response.data;
  }

  public async disconnectConnectivity(platformName: any) {
    let info = `shareability/disconnect-platform?platform=${platformName}`;
    const response = await api.get(`${info}`);
    return response.data;
  }

  public async onSave(data: any) {
    const response = await api.post(`/shareability/save-handle`, data);
    return response.data;
  }

  public async connectDiscordGuild(guildId: any) {
    const response = await api.get(
      `/shareability/connect-guild?guildId=${guildId}`
    );
    return response.data;
  }

  public async brokerageSync() {
    const response = await api.post(`/snaptrade/brokerage_sync`);
    return response.data;
  }

  public async publicProfile(username: string) {
    const response = await api.get(`/public/user/${username}`);
    return response.data;
  }

  public async disconnectDiscordGuild(guildId: any) {
    const response = await api.get(`/shareability/disconnect-guild/${guildId}`);
    return response.data;
  }

  public async snaptradeTransactions(accountId: any) {
    const response = await api.get(
      `/snaptrade/accounts/transactions?accountId=${accountId}`
    );
    return response.data;
  }

  public async snaptradePerformanceStats(accountId: any) {
    const response = await api.get(
      `/snaptrade/get_performance_stats/${accountId}`
    );
    return response.data;
  }
}

export default new HttpService();
